import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../../lib/api';
import { resolveImageUrl } from '../../../lib/utils';

interface HomeSettings {
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  about_title: string;
  about_description: string;
  about_experience_years: number;
  about_happy_customers: number;
  about_services_count: number;
  about_awards: number;
  footer_description: string;
  footer_address: string;
  footer_phone: string;
  footer_email: string;
  footer_facebook: string;
  footer_instagram: string;
  footer_twitter: string;
  working_hours_weekdays: string;
  working_hours_saturday: string;
  working_hours_sunday: string;
  whatsapp_number: string;
  upi_id: string;
  upi_name: string;
}

const HomeSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [showPreview, setShowPreview] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [settings, setSettings] = useState<HomeSettings>({
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    about_title: '',
    about_description: '',
    about_experience_years: 0,
    about_happy_customers: 0,
    about_services_count: 0,
    about_awards: 0,
    footer_description: '',
    footer_address: '',
    footer_phone: '',
    footer_email: '',
    footer_facebook: '',
    footer_instagram: '',
    footer_twitter: '',
    working_hours_weekdays: '',
    working_hours_saturday: '',
    working_hours_sunday: '',
    whatsapp_number: '',
    upi_id: '',
    upi_name: ''
  });

  // Carousel management state
  const [slides, setSlides] = useState<any[]>([]);
  const [editingSlide, setEditingSlide] = useState<any>(null);
  const [showSlideModal, setShowSlideModal] = useState(false);

  const [originalSettings, setOriginalSettings] = useState<HomeSettings | null>(null);

  useEffect(() => {
    loadSettings();
    loadSlides();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Check if user is logged in
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('user');

      if (!token || !user) {
        showNotification('error', 'Please login to access settings');
        navigate('/admin');
        return;
      }

      const userData = JSON.parse(user);
      if (userData.role !== 'admin') {
        showNotification('error', 'Admin access required');
        navigate('/admin');
        return;
      }

      // Debug check - verify backend authentication
      try {
        const userCheck = await apiClient.checkUser();
        console.log('Backend user check:', userCheck);
        if (userCheck.user?.role !== 'admin') {
          showNotification('error', 'Backend reports non-admin role. Please login again.');
          navigate('/admin');
          return;
        }
      } catch (debugError) {
        console.error('Debug check failed:', debugError);
        showNotification('error', 'Authentication verification failed. Please login again.');
        navigate('/admin');
        return;
      }

      const data = await apiClient.getHomeSettings();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error: any) {
      console.error('Failed to load home settings:', error);
      if (error.message === 'Unauthorized') {
        showNotification('error', 'Session expired. Please login again.');
        navigate('/admin');
      } else {
        showNotification('error', 'Failed to load settings. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const loadSlides = async () => {
    try {
      const slidesData = await apiClient.getHeroSlides();
      setSlides(slidesData);
    } catch (error: any) {
      console.error('Failed to load slides:', error);
    }
  };

  const handleSlideEdit = (slide: any) => {
    setEditingSlide({ ...slide });
    setShowSlideModal(true);
  };

  const handleSlideAdd = () => {
    setEditingSlide({
      slide_number: slides.length + 1,
      title: '',
      subtitle: '',
      description: '',
      image_url: '',
      cta_text: 'Book Now',
      cta_link: '/booking'
    });
    setShowSlideModal(true);
  };

  const handleSlideSave = async () => {
    if (!editingSlide) return;

    try {
      if (editingSlide.id) {
        await apiClient.updateHeroSlide(editingSlide.id, editingSlide);
        showNotification('success', 'Slide updated successfully!');
      } else {
        await apiClient.createHeroSlide(editingSlide);
        showNotification('success', 'Slide created successfully!');
      }

      await loadSlides();
      setShowSlideModal(false);
      setEditingSlide(null);
    } catch (error: any) {
      console.error('Failed to save slide:', error);
      showNotification('error', 'Failed to save slide');
    }
  };

  const handleSlideDelete = async (slideId: string) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await apiClient.deleteHeroSlide(slideId);
        showNotification('success', 'Slide deleted successfully!');
        await loadSlides();
      } catch (error: any) {
        console.error('Failed to delete slide:', error);
        showNotification('error', 'Failed to delete slide');
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.currentTarget as HTMLInputElement & HTMLTextAreaElement;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  // Replace existing handleImageUpload with this:
  const handleImageUpload = (field: string) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;

    // limit size check (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showNotification('error', 'Image size must be less than 5MB');
      return;
    }

    try {
      setLoading(true); // optional: show spinner while uploading

      const formData = new FormData();
      // IMPORTANT: backend multer expects field name 'image' to store under uploads/images/
      formData.append('image', file);

      // call the existing api client upload helper
      const res = await apiClient.uploadImage(formData);
      // backend returns { imageUrl, filename } from /home/upload/image route
      const imageUrl = res?.imageUrl || res?.url || res?.image || res?.filename ? (res.imageUrl || `/uploads/images/${res.filename}`) : null;

      if (!imageUrl) {
        showNotification('error', 'Upload succeeded but server returned no image URL.');
        return;
      }

      // set the returned public URL into settings for that field (hero_image, story_image, etc.)
      setSettings(prev => ({
        ...prev,
        [field]: imageUrl
      }));

      showNotification('success', 'Image uploaded successfully.');
    } catch (err: any) {
      console.error('Image upload failed', err);
      const message = err?.message || 'Failed to upload image';
      showNotification('error', `Failed to upload image: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (url: string | null | undefined) => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;
    return resolveImageUrl(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!settings.hero_title || !settings.hero_subtitle) {
      showNotification('error', 'Hero title and subtitle are required');
      return;
    }

    if (!settings.whatsapp_number || !settings.footer_phone || !settings.footer_email) {
      showNotification('error', 'Contact information is required');
      return;
    }

    try {
      setSaving(true);

      // Check authentication before saving
      const token = localStorage.getItem('authToken');
      if (!token) {
        showNotification('error', 'Session expired. Please login again.');
        navigate('/admin');
        return;
      }

      await apiClient.updateHomeSettings(settings);
      setOriginalSettings(settings);
      showNotification('success', 'Settings updated successfully! Changes will reflect on the homepage.');
    } catch (error: any) {
      console.error('Failed to update settings:', error);
      if (error.message === 'Unauthorized' || error.message.includes('403')) {
        showNotification('error', 'Session expired or insufficient permissions. Please login again.');
        navigate('/admin');
      } else {
        showNotification('error', `Failed to update settings: ${error.message}`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings && window.confirm('Are you sure you want to reset all changes?')) {
      setSettings(originalSettings);
      showNotification('success', 'Changes have been reset');
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  const tabs = [
    { id: 'hero', label: 'Hero Section', icon: 'ri-home-line' },
    { id: 'carousel', label: 'Carousel Slides', icon: 'ri-slideshow-3-line' },
    { id: 'about', label: 'About Section', icon: 'ri-information-line' },
    { id: 'contact', label: 'Contact Info', icon: 'ri-phone-line' },
    { id: 'hours', label: 'Working Hours', icon: 'ri-time-line' },
    { id: 'social', label: 'Social Media', icon: 'ri-share-line' },
    { id: 'payment', label: 'Payment', icon: 'ri-bank-card-line' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-400 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-20 z-40">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-arrow-left-line text-xl"></i>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Home Page Settings</h1>
                <p className="text-sm text-gray-600">Manage your homepage content and settings</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {hasChanges && (
                <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  <i className="ri-error-warning-line mr-1"></i>
                  Unsaved Changes
                </span>
              )}
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors flex items-center gap-2"
              >
                <i className={`ri-${showPreview ? 'eye-close' : 'eye'}-line`}></i>
                {showPreview ? 'Hide' : 'Show'} Preview
              </button>
              <button
                onClick={handleReset}
                disabled={!hasChanges || saving}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-restart-line mr-2"></i>
                Reset
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !hasChanges}
                className="px-6 py-2 bg-primary-400 hover:bg-primary-500 text-black rounded-lg font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-black border-t-transparent rounded-full"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`fixed top-24 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 animate-slideInRight ${notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
          <i className={`ri-${notification.type === 'success' ? 'check' : 'error-warning'}-line text-xl`}></i>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-40">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Sections</h3>
              <nav className="space-y-2">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center space-x-3 ${activeTab === tab.id
                        ? 'bg-primary-400 text-black shadow-md transform scale-105'
                        : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <i className={`${tab.icon} text-lg`}></i>
                    <span className="font-medium">{tab.label}</span>
                    {activeTab === tab.id && (
                      <i className="ri-arrow-right-s-line ml-auto"></i>
                    )}
                  </button>
                ))}
              </nav>

              {/* Quick Stats */}
              <div className="mt-6 p-4 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Quick Stats</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Experience</span>
                    <span className="font-semibold">{settings.about_experience_years} years</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Customers</span>
                    <span className="font-semibold">{settings.about_happy_customers.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Services</span>
                    <span className="font-semibold">{settings.about_services_count}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg p-8">
              {/* Hero Section Tab */}
              {activeTab === 'hero' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Hero Section</h2>
                      <p className="text-gray-600">The first thing visitors see on your homepage</p>
                    </div>
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <i className="ri-home-line text-2xl text-primary-600"></i>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hero Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="hero_title"
                        value={settings.hero_title}
                        onChange={handleChange}
                        placeholder="e.g., Elevate Your Beauty"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Main headline on the homepage</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Hero Subtitle <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="hero_subtitle"
                        value={settings.hero_subtitle}
                        onChange={handleChange}
                        placeholder="e.g., Experience luxury and transformation"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all duration-300"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Supporting text below the title</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Hero Background Image
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-400 transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload('hero_image')}
                        className="hidden"
                        id="hero-image-upload"
                      />
                      <label htmlFor="hero-image-upload" className="cursor-pointer">
                        {settings.hero_image ? (
                          <div>
                            <img
                              src={getImageUrl(settings.hero_image)}
                              alt="Hero"
                              className="max-h-32 mx-auto mb-3 rounded-lg shadow-md"
                            />
                            <p className="text-sm text-primary-600 font-medium">Click to change image</p>
                          </div>
                        ) : (
                          <div>
                            <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                            <p className="text-gray-600">Click to upload hero image</p>
                            <p className="text-xs text-gray-500 mt-1">Recommended: 1920x1080px, Max 5MB</p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  {/* Live Preview */}
                  {showPreview && (
                    <div className="mt-6 p-6 bg-gradient-to-r from-gray-900 to-gray-700 rounded-lg text-white">
                      <p className="text-sm text-gray-300 mb-2">Preview</p>
                      <h3 className="text-3xl font-bold mb-2">{settings.hero_title || 'Your Title Here'}</h3>
                      <p className="text-lg text-gray-300">{settings.hero_subtitle || 'Your subtitle here'}</p>
                    </div>
                  )}
                </div>
              )}

              {/* About Section Tab */}
              {activeTab === 'about' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">About Section</h2>
                      <p className="text-gray-600">Information about your salon and achievements</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <i className="ri-information-line text-2xl text-blue-600"></i>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        About Title
                      </label>
                      <input
                        type="text"
                        name="about_title"
                        value={settings.about_title}
                        onChange={handleChange}
                        placeholder="e.g., About Fully A/C Salon"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        About Description
                      </label>
                      <input
                        type="text"
                        name="about_description"
                        value={settings.about_description}
                        onChange={handleChange}
                        placeholder="e.g., Where Beauty Meets Excellence"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                      <i className="ri-bar-chart-box-line mr-2 text-primary-500"></i>
                      Achievement Statistics
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                        <label className="block text-xs font-semibold text-blue-700 mb-2">
                          Years of Experience
                        </label>
                        <input
                          type="number"
                          name="about_experience_years"
                          value={settings.about_experience_years}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-white"
                        />
                      </div>

                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                        <label className="block text-xs font-semibold text-green-700 mb-2">
                          Happy Customers
                        </label>
                        <input
                          type="number"
                          name="about_happy_customers"
                          value={settings.about_happy_customers}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-400 focus:border-transparent bg-white"
                        />
                      </div>

                      <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
                        <label className="block text-xs font-semibold text-purple-700 mb-2">
                          Total Services
                        </label>
                        <input
                          type="number"
                          name="about_services_count"
                          value={settings.about_services_count}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-white"
                        />
                      </div>

                      <div className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
                        <label className="block text-xs font-semibold text-orange-700 mb-2">
                          Awards Won
                        </label>
                        <input
                          type="number"
                          name="about_awards"
                          value={settings.about_awards}
                          onChange={handleChange}
                          className="w-full px-3 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent bg-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Stats Preview */}
                  {showPreview && (
                    <div className="grid grid-cols-4 gap-4 p-6 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-primary-600">{settings.about_experience_years}+</p>
                        <p className="text-sm text-gray-600">Years</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-green-600">{settings.about_happy_customers.toLocaleString()}+</p>
                        <p className="text-sm text-gray-600">Customers</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-purple-600">{settings.about_services_count}+</p>
                        <p className="text-sm text-gray-600">Services</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-orange-600">{settings.about_awards}</p>
                        <p className="text-sm text-gray-600">Awards</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Contact Info Tab */}
              {activeTab === 'contact' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Contact Information</h2>
                      <p className="text-gray-600">How customers can reach you</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-lg">
                      <i className="ri-phone-line text-2xl text-green-600"></i>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-map-pin-line mr-2 text-red-500"></i>
                        Salon Address
                      </label>
                      <input
                        type="text"
                        name="footer_address"
                        value={settings.footer_address}
                        onChange={handleChange}
                        placeholder="e.g., Peepal Chowk, Thakurdwara, Uttar Pradesh"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-phone-fill mr-2 text-blue-500"></i>
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="footer_phone"
                          value={settings.footer_phone}
                          onChange={handleChange}
                          placeholder="+91 9999999999"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-mail-line mr-2 text-purple-500"></i>
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="footer_email"
                          value={settings.footer_email}
                          onChange={handleChange}
                          placeholder="info@salon.com"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-whatsapp-line mr-2 text-green-500"></i>
                        WhatsApp Number (for bookings) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="whatsapp_number"
                        value={settings.whatsapp_number}
                        onChange={handleChange}
                        placeholder="919999999999 (without + or spaces)"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">Format: Country code + number (e.g., 919999999999)</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-file-text-line mr-2 text-indigo-500"></i>
                        Footer Description
                      </label>
                      <textarea
                        name="footer_description"
                        value={settings.footer_description}
                        onChange={handleChange}
                        placeholder="Brief description for the footer"
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                      />
                    </div>
                  </div>

                  {/* Contact Preview */}
                  {showPreview && (
                    <div className="p-6 bg-gray-900 text-white rounded-lg">
                      <h3 className="text-lg font-bold mb-4">Contact Preview</h3>
                      <div className="space-y-3">
                        <p className="flex items-center text-sm">
                          <i className="ri-map-pin-line mr-3 text-primary-400"></i>
                          {settings.footer_address || 'No address set'}
                        </p>
                        <p className="flex items-center text-sm">
                          <i className="ri-phone-line mr-3 text-primary-400"></i>
                          {settings.footer_phone || 'No phone set'}
                        </p>
                        <p className="flex items-center text-sm">
                          <i className="ri-mail-line mr-3 text-primary-400"></i>
                          {settings.footer_email || 'No email set'}
                        </p>
                        <p className="flex items-center text-sm">
                          <i className="ri-whatsapp-line mr-3 text-green-400"></i>
                          {settings.whatsapp_number || 'No WhatsApp set'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Working Hours Tab */}
              {activeTab === 'hours' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Working Hours</h2>
                      <p className="text-gray-600">When your salon is open for business</p>
                    </div>
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <i className="ri-time-line text-2xl text-orange-600"></i>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-calendar-2-line mr-2 text-blue-600"></i>
                        Monday - Friday
                      </label>
                      <input
                        type="text"
                        name="working_hours_weekdays"
                        value={settings.working_hours_weekdays}
                        onChange={handleChange}
                        placeholder="e.g., 9:00 AM - 8:00 PM"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white"
                      />
                    </div>

                    <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-calendar-event-line mr-2 text-green-600"></i>
                        Saturday
                      </label>
                      <input
                        type="text"
                        name="working_hours_saturday"
                        value={settings.working_hours_saturday}
                        onChange={handleChange}
                        placeholder="e.g., 9:00 AM - 9:00 PM"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white"
                      />
                    </div>

                    <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-calendar-check-line mr-2 text-orange-600"></i>
                        Sunday
                      </label>
                      <input
                        type="text"
                        name="working_hours_sunday"
                        value={settings.working_hours_sunday}
                        onChange={handleChange}
                        placeholder="e.g., 10:00 AM - 6:00 PM or Closed"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-white"
                      />
                    </div>
                  </div>

                  {/* Hours Preview */}
                  {showPreview && (
                    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        <i className="ri-time-line mr-2"></i>
                        Business Hours
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="font-medium text-gray-700">Mon - Fri</span>
                          <span className="text-gray-900">{settings.working_hours_weekdays || 'Not set'}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                          <span className="font-medium text-gray-700">Saturday</span>
                          <span className="text-gray-900">{settings.working_hours_saturday || 'Not set'}</span>
                        </div>
                        <div className="flex justify-between py-2">
                          <span className="font-medium text-gray-700">Sunday</span>
                          <span className="text-gray-900">{settings.working_hours_sunday || 'Not set'}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Social Media Tab */}
              {activeTab === 'social' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Social Media Links</h2>
                      <p className="text-gray-600">Connect with customers on social platforms</p>
                    </div>
                    <div className="p-3 bg-pink-100 rounded-lg">
                      <i className="ri-share-line text-2xl text-pink-600"></i>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-facebook-circle-fill mr-2 text-blue-600"></i>
                        Facebook URL
                      </label>
                      <input
                        type="url"
                        name="footer_facebook"
                        value={settings.footer_facebook}
                        onChange={handleChange}
                        placeholder="https://facebook.com/yoursalon"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-instagram-fill mr-2 text-pink-600"></i>
                        Instagram URL
                      </label>
                      <input
                        type="url"
                        name="footer_instagram"
                        value={settings.footer_instagram}
                        onChange={handleChange}
                        placeholder="https://instagram.com/yoursalon"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-twitter-fill mr-2 text-blue-400"></i>
                        Twitter URL
                      </label>
                      <input
                        type="url"
                        name="footer_twitter"
                        value={settings.footer_twitter}
                        onChange={handleChange}
                        placeholder="https://twitter.com/yoursalon"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Social Preview */}
                  {showPreview && (
                    <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Social Media Links</h3>
                      <div className="flex space-x-4">
                        {settings.footer_facebook && (
                          <a href={settings.footer_facebook} target="_blank" rel="noopener noreferrer"
                            className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                            <i className="ri-facebook-fill text-xl"></i>
                          </a>
                        )}
                        {settings.footer_instagram && (
                          <a href={settings.footer_instagram} target="_blank" rel="noopener noreferrer"
                            className="p-3 bg-gradient-to-br from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all">
                            <i className="ri-instagram-fill text-xl"></i>
                          </a>
                        )}
                        {settings.footer_twitter && (
                          <a href={settings.footer_twitter} target="_blank" rel="noopener noreferrer"
                            className="p-3 bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors">
                            <i className="ri-twitter-fill text-xl"></i>
                          </a>
                        )}
                        {!settings.footer_facebook && !settings.footer_instagram && !settings.footer_twitter && (
                          <p className="text-gray-500">No social media links configured</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Carousel Tab */}
              {activeTab === 'carousel' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Carousel Management</h2>
                      <p className="text-gray-600">Manage the hero carousel slides on your homepage</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="p-3 bg-purple-100 rounded-lg">
                        <i className="ri-slideshow-3-line text-2xl text-purple-600"></i>
                      </div>
                      <button
                        onClick={handleSlideAdd}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                      >
                        <i className="ri-add-line"></i>
                        Add Slide
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-6">
                    {slides.map((slide, index) => (
                      <div key={slide.id} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border border-gray-200">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold">
                              {slide.slide_number}
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-gray-900">{slide.title}</h3>
                              <p className="text-sm text-gray-600">{slide.subtitle}</p>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSlideEdit(slide)}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => handleSlideDelete(slide.id)}
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="md:col-span-1">
                            {slide.image_url ? (
                              <img src={getImageUrl(slide.image_url)} alt={slide.title} className="w-16 h-16 object-cover rounded" />
                            ) : (
                              <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
                                <i className="ri-image-line text-gray-400 text-2xl"></i>
                              </div>
                            )}
                          </div>
                          <div className="md:col-span-2">
                            <p className="text-gray-700 mb-3">{slide.description}</p>
                            <div className="flex items-center space-x-4">
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                                {slide.cta_text}
                              </span>
                              <span className="text-sm text-gray-500">→ {slide.cta_link}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {slides.length === 0 && (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <i className="ri-slideshow-3-line text-4xl text-gray-400 mb-4"></i>
                        <p className="text-gray-600 mb-4">No slides configured yet</p>
                        <button
                          onClick={handleSlideAdd}
                          className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
                        >
                          Create Your First Slide
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Carousel Preview */}
                  {showPreview && slides.length > 0 && (
                    <div className="p-6 bg-gradient-to-r from-gray-900 to-gray-700 rounded-lg text-white">
                      <h3 className="text-lg font-bold mb-4">Carousel Preview</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {slides.map((slide, index) => (
                          <div key={slide.id} className="bg-black/50 rounded-lg p-4">
                            <h4 className="font-bold text-primary-400">{slide.subtitle}</h4>
                            <h3 className="text-xl font-bold mb-2">{slide.title}</h3>
                            <p className="text-sm text-gray-300 mb-3">{slide.description}</p>
                            <span className="px-3 py-1 bg-primary-400 text-black rounded-full text-xs font-bold">
                              {slide.cta_text}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Tab */}
              {activeTab === 'payment' && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Payment Settings</h2>
                      <p className="text-gray-600">Configure UPI payment details</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-lg">
                      <i className="ri-bank-card-line text-2xl text-green-600"></i>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-qr-code-line mr-2 text-indigo-600"></i>
                        UPI ID
                      </label>
                      <input
                        type="text"
                        name="upi_id"
                        value={settings.upi_id}
                        onChange={handleChange}
                        placeholder="yourbusiness@upi"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Your UPI payment ID for receiving payments</p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <i className="ri-store-2-line mr-2 text-green-600"></i>
                        Business Name (for UPI)
                      </label>
                      <input
                        type="text"
                        name="upi_name"
                        value={settings.upi_name}
                        onChange={handleChange}
                        placeholder="Your Salon Name"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-400 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">Name shown in UPI payment apps</p>
                    </div>
                  </div>

                  {/* Payment Preview */}
                  {showPreview && (
                    <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">
                        <i className="ri-secure-payment-line mr-2"></i>
                        Payment Information
                      </h3>
                      <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-600">UPI ID:</span>
                          <span className="font-mono text-sm text-gray-900">{settings.upi_id || 'Not configured'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Business Name:</span>
                          <span className="text-sm text-gray-900">{settings.upi_name || 'Not configured'}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-3">
                        <i className="ri-information-line mr-1"></i>
                        These details will be used for QR code generation in booking page
                      </p>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      {/* Slide Edit Modal */}
      {showSlideModal && editingSlide && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">
                {editingSlide.id ? 'Edit Slide' : 'Add New Slide'}
              </h3>
              <button
                onClick={() => setShowSlideModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Slide Number
                  </label>
                  <input
                    type="number"
                    value={editingSlide.slide_number}
                    onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, slide_number: parseInt(e.currentTarget.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    CTA Text
                  </label>
                  <input
                    type="text"
                    value={editingSlide.cta_text}
                    onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, cta_text: e.currentTarget.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={editingSlide.title}
                  onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, title: e.currentTarget.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={editingSlide.subtitle}
                  onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, subtitle: e.currentTarget.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={editingSlide.description}
                  onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, description: e.currentTarget.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Background Image
                </label>
                <div className="space-y-3">
                  <input
                    type="url"
                    value={editingSlide.image_url}
                    onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, image_url: e.currentTarget.value }))}
                    placeholder="Enter image URL or upload below"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                  />

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.currentTarget.files?.[0];
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            showNotification('error', 'Image size must be less than 5MB');
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            const result = (event.target as FileReader | null)?.result as string | undefined;
                            if (result) {
                              setEditingSlide((prev: any) => ({
                                ...prev,
                                image_url: result
                              }));
                            }
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                      id="slide-image-upload"
                    />
                    <label htmlFor="slide-image-upload" className="cursor-pointer">
                      {editingSlide.image_url ? (
                        <div>
                          <img
                            src={getImageUrl(editingSlide.image_url)}
                            alt="Preview"
                            className="max-h-32 mx-auto mb-2 rounded-lg"
                          />
                          <p className="text-sm text-purple-600">Click to change image</p>
                        </div>
                      ) : (
                        <div>
                          <i className="ri-image-add-line text-3xl text-gray-400 mb-2"></i>
                          <p className="text-gray-600">Click to upload image</p>
                          <p className="text-xs text-gray-500">Recommended: 1920x1080px, Max 5MB</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Call-to-Action Link
                </label>
                <select
                  value={editingSlide.cta_link}
                  onChange={(e) => setEditingSlide((prev: any) => ({ ...prev, cta_link: e.currentTarget.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400"
                >
                  <option value="/booking">Booking Page</option>
                  <option value="/services">Services Page</option>
                  <option value="/gallery">Gallery Page</option>
                  <option value="/contact">Contact Page</option>
                  <option value="/about">About Page</option>
                </select>
              </div>

              {/* Preview */}
              <div className="p-4 bg-gradient-to-r from-gray-900 to-gray-700 rounded-lg text-white">
                <h4 className="text-sm font-semibold text-gray-300 mb-2">Preview</h4>
                <h3 className="text-primary-400 text-lg font-semibold">{editingSlide.subtitle || 'Subtitle'}</h3>
                <h2 className="text-2xl font-bold mb-2">{editingSlide.title || 'Title'}</h2>
                <p className="text-gray-300 mb-3">{editingSlide.description || 'Description'}</p>
                <span className="px-3 py-1 bg-primary-400 text-black rounded-full text-sm font-bold">
                  {editingSlide.cta_text || 'CTA'}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-8">
              <button
                onClick={() => setShowSlideModal(false)}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSlideSave}
                className="px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-xl font-semibold transition-colors"
              >
                {editingSlide.id ? 'Update' : 'Create'} Slide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeSettingsPage;
